import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import type { OrgDashboardTenant } from "../../../api/client.ts";
import { pathForTenant } from "../../../contexts/TenantContext.tsx";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import Sparkline from "../../Sparkline.tsx";
import { TenantAvatar } from "../../TenantAvatar.tsx";
import { ProgressRing } from "./OnboardingPanel.tsx";

/** One card per tenant: its queues, the last two weeks of finds and, while unfinished, its onboarding. */
export default function TenantCards({ tenants }: { tenants: OrgDashboardTenant[] }) {
  return (
    <Panel title="Tenants" count={tenants.length} subtitle="Where each tenant's prospecting stands">
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {tenants.map((t) => {
          const steps = t.onboarding ?? [];
          const done = steps.filter((s) => s.done).length;
          return (
            <Card key={t.id} variant="outlined">
              <CardActionArea component={Link} to={pathForTenant(t.slug, "/prospects")} sx={{ p: 1.75, height: "100%" }}>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 1.5 }}>
                  <TenantAvatar name={t.name} logoUrl={t.logo_url} size={28} />
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                    {t.name}
                  </Typography>
                  {!t.owned && <Chip size="small" variant="outlined" label="Shared" />}
                  {t.owned && done < steps.length && <ProgressRing done={done} total={steps.length} size={30} />}
                </Stack>
                <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end" }}>
                  <Figure label="To decide" value={t.inbox} warn={t.inbox > 0} />
                  <Figure label="Pursuing" value={t.worklist} />
                  <Box sx={{ ml: "auto !important" }}>
                    <Sparkline
                      values={t.daily_hits_14d}
                      variant="area"
                      width={84}
                      height={26}
                      showRange={false}
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", textAlign: "right" }}>
                      {formatNumber(t.daily_hits_14d.reduce((a, b) => a + b, 0))} found · 14 days
                    </Typography>
                  </Box>
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Panel>
  );
}

function Figure({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <Box>
      <Typography
        sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", color: warn ? "warning.main" : "text.primary" }}
      >
        {formatNumber(value)}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
    </Box>
  );
}
